import {
  CalendarToday,
  CreateOutlined,
  DeleteOutline,
  Language,
  MailOutline,
  PageviewOutlined,
  PersonOutline,
  Schedule,
  VideocamOffOutlined,
} from "@mui/icons-material";
import { Box, Button, Grid, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadEnrolments } from "../../../actions/Beneficiaries";
import { ROUTES } from "../../../constants/Routes";
import { ENROLMENT_WORKFLOW_STATUS } from "../../../constants/common";
import {
  currentUserIsReadOnlySelector,
  enrolmentsCountSelector,
  enrolmentsLoadingSelector,
  enrolmentsSelector,
  timezoneSelector,
} from "../../../reducers/selectors";
import IntlMessages from "../../../util/IntlMessages";
import { getDateStr, getTimeSlotStr } from "../../../util/schedule";
import { getFullName } from "../../../util/user";
import { SideBar } from "../../generic/SideBar";
import { EnrolmentsShortList } from "../EnrolmentsShortList";
import { DetailItem } from "./DetailItem";

const useStyles = makeStyles(() => ({
  appointmentTitle: {
    fontWeight: "700",
  },
  localTime: {
    fontSize: "1rem",
    marginTop: "20px",
    marginBottom: "40px",
  },
  appointmentActionsBox: {
    width: "100%",
  },
  appointmentActionButton: {
    padding: "12px 16px",
    textTransform: "none",
  },
}));

const AppointmentDetails = ({
  appointment,
  openDelete,
  deleteAppointment,
  openEditAppointment,
  joinVideoCall,
  loadingJoin,
  canJoinVideoCall,
  bookAppointment,
  toggleDrawer,
}) => {
  // #region Hooks
  const classes = useStyles();
  const dispatch = useDispatch();
  const [hasOnboarding, setHasOnboarding] = useState(!!appointment?.onboarding);
  const [isPast, setIsPast] = useState(false);
  const [agentDetails, setAgentDetails] = useState([]);
  const [beneficiaryDetails, setBeneficiaryDetails] = useState([]);
  const enrolments = useSelector(enrolmentsSelector);
  const enrolmentsLoading = useSelector(enrolmentsLoadingSelector);
  const count = useSelector(enrolmentsCountSelector);
  const timezone = useSelector(timezoneSelector);
  const userIsReadOnly = useSelector(currentUserIsReadOnlySelector);
  const defaultPagination = { ordering: "-created", size: 10 };
  const defaultFilters = {
    workflow_status: ENROLMENT_WORKFLOW_STATUS.REQUIRES_ATTENTION,
  };
  const assignedToAgentFilter = {
    workflow_status: ENROLMENT_WORKFLOW_STATUS.AGENT_ASSIGNED,
    assigned_operator_id: appointment?.operator?.id,
  };
  const [currentPagination, setCurrentPagination] = useState(defaultPagination);
  const [selectedEnrolment, setSelectedEnrolment] = useState(null);

  const scheduleTimezone = timezone === "local" ? dayjs.tz.guess() : timezone;
  const dateScheduleTz = getDateStr(appointment, scheduleTimezone);
  const timeScheduleTz = getTimeSlotStr(appointment, scheduleTimezone);

  useEffect(() => {
    setAppointmentDetailsData();
  }, [appointment]);
  // #endregion

  // #region Load enrolments
  const fetchEnrolments = (pagination, search, assignedToAgent) => {
    if (!hasOnboarding && pagination && !enrolmentsLoading) {
      setCurrentPagination(pagination);

      // If page didn't change, reset it
      if (pagination.page && pagination.page === currentPagination.page) {
        pagination.page = 1;
      }
      const filters = assignedToAgent ? assignedToAgentFilter : defaultFilters;
      dispatch(loadEnrolments({ ...pagination, ...filters, search }));
    }
  };
  // #endregion

  // #region Actions methods
  const openEnrolment = () => {
    const enrolmentId = appointment?.onboarding?.id;
    const url = `${window.location.origin}/app/${ROUTES.ENROLMENT}/${enrolmentId}`;
    window.open(url, "_blank");
  };
  // #endregion

  // #region Aux methods
  function setAppointmentDetailsData() {
    setHasOnboarding(!!appointment?.onboarding);
    setIsPast(getAppointmentIsInPast());
    setAgentDetails(getAgentDetailsArray());
    if (appointment?.onboarding) {
      setBeneficiaryDetails(getBeneficiaryDetailsArray());
    }
  }

  function getAgentDetailsArray() {
    const agentTimezone = appointment?.operator?.timezone;

    return [
      {
        key: "name",
        icon: <PersonOutline />,
        label: "pages.schedule.appointment.agentDetails.name",
        value: getOperatorFullName(),
      },
      {
        key: "day",
        icon: <CalendarToday />,
        label: "pages.schedule.appointment.agentDetails.day",
        value: getDateStr(appointment, agentTimezone),
      },
      {
        key: "time",
        icon: <Schedule />,
        label: "pages.schedule.appointment.agentDetails.time",
        value: getTimeSlotStr(appointment, agentTimezone),
      },
      {
        key: "timezone",
        icon: <Language />,
        label: "pages.schedule.appointment.agentDetails.timezone",
        value: agentTimezone || "N/D",
      },
    ];
  }

  function getBeneficiaryDetailsArray() {
    const { username, timezone: beneficiaryTimezone } =
      appointment?.onboarding?.user || {};

    return [
      {
        key: "name",
        icon: <PersonOutline />,
        label: "pages.schedule.appointment.agentDetails.name",
        value: getBeneficiaryFullName(),
      },
      {
        key: "email",
        icon: <MailOutline />,
        label: "pages.schedule.appointment.beneficiaryDetails.email",
        value: username,
      },
      {
        key: "time",
        icon: <Schedule />,
        label: "pages.schedule.appointment.agentDetails.time",
        value: getTimeSlotStr(appointment, beneficiaryTimezone),
      },
      {
        key: "timezone",
        icon: <Language />,
        label: "pages.schedule.appointment.agentDetails.timezone",
        value: beneficiaryTimezone || "N/D",
      },
    ];
  }

  function getOperatorFullName() {
    return getFullName(appointment?.operator);
  }

  function getBeneficiaryFullName() {
    return getFullName(appointment?.onboarding?.user);
  }

  function getAppointmentIsInPast() {
    const startDate = dayjs(appointment?.slot?.start_datetime);
    return startDate.isBefore(new Date(), "day");
  }
  // #endregion

  //#region Render methods
  const AppointmentDetails = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 1 }}>
        <CalendarToday sx={{ width: "1.25em", color: "primary.main" }} />
        <Typography>{dateScheduleTz}</Typography>
      </Box>
      <TimeScheduleTz />
    </Box>
  );

  const TimeScheduleTz = () => (
    <Box
      sx={{
        display: "inline-flex",
        alignSelf: "flex-start",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.5,
        backgroundColor: "grey.50",
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: "10px",
      }}
    >
      <Language sx={{ fontSize: "1rem", color: "text.secondary" }} />
      <Typography variant="body2">
        {scheduleTimezone}&nbsp;&nbsp;|&nbsp;&nbsp;{timeScheduleTz}
      </Typography>
    </Box>
  );

  const AgentDetails = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        <IntlMessages id="pages.schedule.appointment.agentDetails.title" />
      </Typography>
      {agentDetails && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {agentDetails.map((detail) => {
            return (
              <DetailItem
                key={detail.key}
                icon={detail.icon}
                label={detail.label}
                value={detail.value}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );

  const BeneficiaryDetails = () => (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          <IntlMessages id="pages.schedule.appointment.beneficiaryDetails.title" />
        </Typography>
        {beneficiaryDetails && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {beneficiaryDetails.map((detail) => {
              return (
                <DetailItem
                  key={detail.key}
                  icon={detail.icon}
                  label={detail.label}
                  value={detail.value}
                />
              );
            })}
          </Box>
        )}
      </Box>
      {/* Appointment actions */}
      <Box className={classes.appointmentActionsBox}>
        <Grid container spacing={2}>
          <Grid item xs={12} className="">
            <Button
              variant="outlined"
              color="primary"
              className={classes.appointmentActionButton}
              endIcon={<PageviewOutlined />}
              onClick={() => openEnrolment(appointment)}
              fullWidth
              disableElevation
            >
              <IntlMessages id="pages.schedule.appointment.openEnrolment" />
            </Button>
          </Grid>
          <Grid item xs={12} className="">
            <Button
              variant="contained"
              color="primary"
              className={classes.appointmentActionButton}
              endIcon={<VideocamOffOutlined />}
              onClick={() => joinVideoCall(appointment)}
              disabled={loadingJoin || !canJoinVideoCall || userIsReadOnly}
              fullWidth
              disableElevation
            >
              <IntlMessages id="pages.schedule.appointment.startVideoCall" />
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  );

  const Enrolments = React.useMemo(
    () => (
      <Box>
        <Typography variant="h6" className={classes.appointmentTitle}>
          <IntlMessages id="pages.schedule.slotAvailable.addAppointment.title" />
        </Typography>
        <EnrolmentsShortList
          appointment={appointment}
          enrolments={enrolments}
          defaultPagination={defaultPagination}
          fetchEnrolments={fetchEnrolments}
          count={count}
          selectedEnrolmentChange={setSelectedEnrolment}
        />
      </Box>
    ),
    [appointment, enrolments, count, defaultPagination]
  );
  //#endregion

  return (
    <>
      <SideBar
        title={`pages.schedule.${
          hasOnboarding ? "appointment" : "slotAvailable"
        }.title`}
        toggleDrawer={toggleDrawer}
        cancelLabel={`pages.schedule.${
          hasOnboarding ? "appointment.delete" : "slotAvailable.remove"
        }`}
        cancelFn={() =>
          hasOnboarding
            ? openDelete(appointment)
            : deleteAppointment(appointment)
        }
        cancelIcon={<DeleteOutline />}
        cancelDisabled={isPast || userIsReadOnly}
        saveLabel={`pages.schedule.${
          hasOnboarding ? "appointment.edit" : "slotAvailable.confirm"
        }`}
        saveFn={() =>
          hasOnboarding
            ? openEditAppointment(appointment)
            : bookAppointment(appointment, selectedEnrolment)
        }
        saveIcon={hasOnboarding ? <CreateOutlined /> : null}
        saveProps={{
          variant: hasOnboarding ? "text" : "contained",
          style: { justifyContent: hasOnboarding ? "start" : "none" },
        }}
        saveDisabled={
          (!hasOnboarding && !selectedEnrolment) || isPast || userIsReadOnly
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <AppointmentDetails />
          <AgentDetails />
          {hasOnboarding ? <BeneficiaryDetails /> : Enrolments}
        </Box>
      </SideBar>
    </>
  );
};

export { AppointmentDetails };
